import i18next from 'i18next'
import { ReactNode, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { SocialMatchFormat } from 'types/forms/post-score'
import { FastFormatManager } from './score-validators/FastFormatManager'
import { ScoreManager } from './score-validators/ScoreManager'

export const POST_SCORE_ALLOWED_DATE_LIMIT = 14

const SOCIAL_MATCH_FORMAT_DATA = [
  {
    value: SocialMatchFormat.THREE_SETS_SIX_GAMES,
    translationKey: 'postScore.matchFormat.options.3s6g.name',
  },
  {
    value: SocialMatchFormat.TWO_SETS_SIX_GAMES,
    translationKey: 'postScore.matchFormat.options.2s6g.name',
  },
  {
    value: SocialMatchFormat.TWO_SETS_FOUR_GAMES,
    translationKey: 'postScore.matchFormat.options.2s4g.name',
  },
  {
    value: SocialMatchFormat.ONE_SET_SIX_GAMES,
    translationKey: 'postScore.matchFormat.options.1s6g.name',
  },
  {
    value: SocialMatchFormat.TEN_POINTS,
    translationKey: 'postScore.matchFormat.options.10p.name',
  },
]

export const useSocialMatchFormatOptions = () => {
  const { t } = useTranslation('strSocialMatches')

  return useMemo(
    () =>
      SOCIAL_MATCH_FORMAT_DATA.map(({ value, translationKey }) => ({
        value: value,
        label: t(translationKey),
      })),
    [t]
  )
}

export const socialMatchTypeHelperText: Record<SocialMatchFormat, () => ReactNode> = {
  [SocialMatchFormat.THREE_SETS_SIX_GAMES]: () => (
    <Trans ns='strSocialMatches' i18nKey='postScore.matchFormat.options.3s6g.helperText'>
      <p>
        You play up to three tie-break sets to six games – first player to win two, wins the match.
      </p>
      <p>
        Each set must be by two clear games (e.g., 6-4) – if the score goes to 6-6, then you play a
        seven point tie-break to decide the winner.
      </p>
    </Trans>
  ),
  [SocialMatchFormat.TWO_SETS_SIX_GAMES]: () => (
    <Trans ns='strSocialMatches' i18nKey='postScore.matchFormat.options.2s6g.helperText'>
      <p>
        You play two tie-break sets to six games, with a match tie-break (up to 10 points) to decide
        the winner at one set all.
      </p>
      <p>
        Each set must be by two clear games (e.g., 6-4) – if the score goes to 6-6, then you play a
        seven point tie-break to decide the winner.
      </p>
      <p>The match tie-break is a race to 10 points but must also be won by two clear points</p>
    </Trans>
  ),
  [SocialMatchFormat.TWO_SETS_FOUR_GAMES]: () => (
    <Trans ns='strSocialMatches' i18nKey='postScore.matchFormat.options.2s4g.helperText'>
      <p>
        You play two short tie-break sets to four games, with a match tie-break (up to 10 points) to
        decide the winner at one set all.
      </p>
      <p>
        Each set must be by two clear games (e.g., 4-2) – if the score goes to 3-3, then you play a
        seven point tie-break to decide the winner.
      </p>
      <p>The match tie-break is a race to 10 points but must also be won by two clear points.</p>
    </Trans>
  ),
  [SocialMatchFormat.ONE_SET_SIX_GAMES]: () => (
    <Trans ns='strSocialMatches' i18nKey='postScore.matchFormat.options.1s6g.helperText'>
      <p>You play one tie-break set to six games – whoever wins, claims the match.</p>
      <p>
        The set must be by two clear games (e.g., 6-4) – if the score goes to 6-6, then you play a
        seven point tie-break to decide the winner.
      </p>
    </Trans>
  ),
  [SocialMatchFormat.TEN_POINTS]: () => (
    <Trans ns='strSocialMatches' i18nKey='postScore.matchFormat.options.10p.helperText'>
      <p>
        You play first to 10 points in a tie-break format – alternating server after the first point
        and then every two points for the rest of the match.
      </p>
      <p>
        You can only win by two clear points, so if the score goes to 9-9 you keep playing, e.g. you
        could win 18-16.
      </p>
    </Trans>
  ),
}

export const MatchFormatManagers = {
  [SocialMatchFormat.THREE_SETS_SIX_GAMES]: new ScoreManager({
    match: { sets: 3, tiebreaks: 0, superTiebreaks: 0 },
    set: { minPointsDifference: 2, pointsMax: 7, pointsToWin: 6 },
    tiebreak: { minPointsDifference: 2, pointsMax: 99, pointsToWin: 7 },
  }),
  [SocialMatchFormat.TWO_SETS_SIX_GAMES]: new ScoreManager({
    match: { sets: 2, tiebreaks: 0, superTiebreaks: 1 },
    set: { minPointsDifference: 2, pointsMax: 7, pointsToWin: 6 },
    tiebreak: { minPointsDifference: 2, pointsMax: 99, pointsToWin: 7 },
    superTiebreak: { minPointsDifference: 2, pointsMax: 99, pointsToWin: 10 },
  }),
  [SocialMatchFormat.TWO_SETS_FOUR_GAMES]: new FastFormatManager({
    match: { sets: 2, tiebreaks: 0, superTiebreaks: 1 },
    set: { minPointsDifference: 2, pointsMax: 4, pointsToWin: 4 },
    tiebreak: { minPointsDifference: 2, pointsMax: 99, pointsToWin: 7 },
    superTiebreak: { minPointsDifference: 2, pointsMax: 99, pointsToWin: 10 },
  }),
  [SocialMatchFormat.ONE_SET_SIX_GAMES]: new ScoreManager({
    match: { sets: 1, tiebreaks: 0, superTiebreaks: 0 },
    set: { minPointsDifference: 2, pointsMax: 7, pointsToWin: 6 },
    tiebreak: { minPointsDifference: 2, pointsMax: 99, pointsToWin: 7 },
  }),
  [SocialMatchFormat.TEN_POINTS]: new ScoreManager({
    match: { sets: 0, tiebreaks: 1, superTiebreaks: 0 },
    set: { minPointsDifference: 2, pointsMax: 5, pointsToWin: 4 },
    tiebreak: { minPointsDifference: 2, pointsMax: 99, pointsToWin: 10 },
    superTiebreak: { minPointsDifference: 2, pointsMax: 99, pointsToWin: 10 },
  }),
} as const
